import { useState, useEffect } from 'react';
import { Combobox, Portal, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { usePopper } from 'react-popper';
import { Fragment } from 'react';

interface Props {
    value: string;
    onChange: (value: string) => void;
    options: string[];
    placeholder?: string;
    className?: string;
    allowCustom?: boolean;
    disabled?: boolean;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export default function CustomCombobox({
    value,
    onChange,
    options,
    placeholder = 'Seleccionar...',
    className = '',
    allowCustom = false,
    disabled = false,
    onKeyDown
}: Props) {
    const [query, setQuery] = useState('');
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [
            { name: 'offset', options: { offset: [0, 2] } },
            { name: 'preventOverflow', options: { padding: 8 } }
        ]
    });

    const filteredOptions =
        query === ''
            ? options
            : options
                .filter((option) =>
                    option && String(option)
                        .toLowerCase()
                        .includes(query.toLowerCase().trim())
                )
                .sort((a, b) => {
                    const normalizedA = String(a).toLowerCase();
                    const normalizedB = String(b).toLowerCase();
                    const normalizedQuery = query.toLowerCase().trim();
                    
                    if (normalizedA === normalizedQuery) return -1;
                    if (normalizedB === normalizedQuery) return 1;
                    
                    if (normalizedA.startsWith(normalizedQuery) && !normalizedB.startsWith(normalizedQuery)) return -1;
                    if (normalizedB.startsWith(normalizedQuery) && !normalizedA.startsWith(normalizedQuery)) return 1;
                    
                    return normalizedA.localeCompare(normalizedB);
                });

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();

            if (query.trim() === '') return;

            const normalizedQuery = query.toLowerCase().replace(/\s+/g, '');
            const exactMatch = filteredOptions.find(
                opt => opt.toLowerCase().replace(/\s+/g, '') === normalizedQuery
            );

            if (exactMatch) {
                onChange(exactMatch);
                setQuery(exactMatch);
                event.currentTarget.blur();
            } else if (allowCustom) {
                onChange(query);
                // Keep query as is since it's custom
                event.currentTarget.blur();
            } else if (filteredOptions.length > 0) {
                onChange(filteredOptions[0]);
                setQuery(filteredOptions[0]);
                event.currentTarget.blur();
            }
            // If allowCustom is false and no options, do nothing (strict mode)
        }
    };

    // Keep query synced with external value changes
    useEffect(() => {
        setQuery(value || '');
    }, [value]);

    return (
        <div className={`relative ${className}`}>
            <Combobox value={value} onChange={onChange} disabled={disabled} nullable>
                <div className="relative" ref={setReferenceElement}>
                    <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left border border-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
                        <Combobox.Input
                            className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 placeholder:text-gray-400"
                            displayValue={(item: string) => item}
                            onChange={(event) => {
                                setQuery(event.target.value);
                                if (allowCustom) {
                                    onChange(event.target.value);
                                }
                            }}
                            onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                e.target.select();
                            }}
                            onBlur={() => {
                                if (allowCustom && query !== value && query.trim() !== '') {
                                    onChange(query);
                                } else if (!allowCustom && query !== value) {
                                    setQuery(value || '');
                                } else if (allowCustom && query.trim() === '') {
                                    onChange('');
                                }
                            }}
                            onKeyDown={(e) => {
                                handleKeyDown(e);
                                if (onKeyDown) onKeyDown(e);
                            }}
                            placeholder={placeholder}
                            autoComplete="off"
                            // Esto fuerza a que si el input se borra, React permita teclear fluido
                            value={query || value || ''}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                            />
                        </Combobox.Button>
                    </div>
                    <Portal>
                        <div
                            ref={setPopperElement}
                            style={{ ...styles.popper, minWidth: referenceElement?.offsetWidth }}
                            {...attributes.popper}
                            className="z-[10000]"
                        >
                            <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <Combobox.Options className="max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-xl ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                    {filteredOptions.length === 0 && query !== '' ? (
                                        allowCustom ? (
                                            <Combobox.Option
                                                className={({ active }) =>
                                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                                    }`
                                                }
                                                value={query}
                                            >
                                                {({ active }) => (
                                                    <div className="flex justify-between items-center">
                                                        <span className="truncate">Crear "{query}"</span>
                                                        <span className={`text-xs ml-2 shrink-0 opacity-70 ${active ? 'text-white' : 'text-gray-500'}`}>
                                                            (Nuevo)
                                                        </span>
                                                    </div>
                                                )}
                                            </Combobox.Option>
                                        ) : (
                                            <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                                                No encontrado.
                                            </div>
                                        )
                                    ) : (
                                        filteredOptions.map((option) => (
                                            <Combobox.Option
                                                key={option}
                                                className={({ active }) =>
                                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                                    }`
                                                }
                                                value={option}
                                            >
                                                {({ selected, active }) => (
                                                    <>
                                                        <span
                                                            className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                                                                }`}
                                                        >
                                                            {option}
                                                        </span>
                                                        {selected ? (
                                                            <span
                                                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'
                                                                    }`}
                                                            >
                                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                            </span>
                                                        ) : null}
                                                    </>
                                                )}
                                            </Combobox.Option>
                                        ))
                                    )}
                                </Combobox.Options>
                            </Transition>
                        </div>
                    </Portal>
                </div>
            </Combobox>
        </div>
    );
}
